// src/components/ui/LandingKPICard/LandingKPICard.jsx
import React from "react"
import { cva } from "class-variance-authority"
import { cn } from "../../../lib/utils"

/**
 * LandingKPICard — dashboard summary card with accent bar, icon, trend badge.
 * Linear/Vercel OLED aesthetic with accent-bar top edge.
 */
const landingKpiCardVariants = cva(
  "relative cursor-default overflow-hidden rounded-[var(--radius-lg)] border border-solid border-[var(--gray-4)] bg-[var(--color-panel-solid)] p-5 opacity-0 shadow-[var(--shadow-sm)] animate-kpi-card-reveal motion-reduce:opacity-100 motion-reduce:animate-none transition-[box-shadow,transform] duration-200 ease-[ease] hover:-translate-y-px hover:shadow-[var(--shadow-md)] before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-[3px] before:rounded-t-[var(--radius-lg)] dark:bg-transparent dark:shadow-none dark:hover:bg-[rgba(255,255,255,0.02)] dark:hover:shadow-[var(--shadow-md)]",
  {
    variants: {
      accent: {
        green: "before:bg-[var(--brand-green)]",
        blue: "before:bg-[var(--accent-blue)]",
        amber: "before:bg-[var(--accent-amber)]",
        red: "before:bg-[var(--accent-red)]",
      },
    },
    defaultVariants: {
      accent: "green",
    },
  },
)

const kpiIconWrapVariants = cva(
  "flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-md)]",
  {
    variants: {
      accent: {
        green: "bg-[var(--green-tint-light)] text-[var(--brand-green)]",
        blue: "bg-[var(--accent-blue-light)] text-[var(--accent-blue)]",
        amber: "bg-[var(--accent-amber-light)] text-[var(--accent-amber)]",
        // Dark: lighten the red tint so it reads on near-black (dark
        // --accent-red-light resolves lighter than the light-mode tint).
        red: "bg-[var(--accent-red-light)] text-[var(--accent-red)] dark:bg-[rgba(220,38,38,0.15)]",
      },
    },
    defaultVariants: {
      accent: "green",
    },
  },
)

const kpiTrendVariants = cva(
  "inline-flex items-center gap-0.5 whitespace-nowrap rounded-[10px] px-[7px] py-0.5 text-[length:var(--font-2xs)] font-semibold",
  {
    variants: {
      direction: {
        up: "bg-[var(--green-tint-light)] text-[var(--green-hover)] dark:bg-[rgba(0,230,118,0.12)] dark:text-[var(--brand-green)]",
        down: "bg-[var(--accent-red-light)] text-[var(--accent-red)] dark:bg-[rgba(220,38,38,0.15)]",
        flat: "bg-[var(--accent-blue-light)] text-[var(--accent-blue)]",
      },
    },
    defaultVariants: {
      direction: "flat",
    },
  },
)

/* Skeleton pulse box — sized per slot at the use site */
const kpiSkeletonBoxClassName =
  "rounded-[var(--radius-sm)] bg-[var(--gray-3)] animate-skeleton-pulse dark:bg-[rgba(255,255,255,0.06)]"

const kpiIconRowClassName = "mb-3 flex items-center justify-between"

/* ── Shared layout classes for the grid wrapper / section label
      around the cards (used by LandingPageDashboardMain,
      SuperAdminHome, MyFacility) ─────────────────────────────── */
const landingKpiGridClassName =
  "grid grid-cols-4 gap-5 mb-6 px-4 pt-4 max-[767px]:grid-cols-2 max-[767px]:gap-3"

const landingKpiSectionLabelClassName =
  "mb-3 text-[length:var(--font-2xs)] font-bold uppercase tracking-[0.08em] text-[var(--text-muted-2)]"

/**
 * accent: "green" | "blue" | "amber" | "red"
 * icon:   Lucide element (already sized, e.g. <Building2 size={18} />)
 * value:  number or node displayed as the headline figure
 * label:  short descriptor below the value
 * trend:  { direction: "up"|"down"|"flat", label: string }
 * subNote: small helper text at the bottom
 * loading: renders animated pulse skeleton when true
 */
const LandingKPICard = React.forwardRef(
  (
    {
      accent = "green",
      icon,
      value,
      label,
      trend,
      subNote,
      loading,
      style,
      className,
    },
    ref,
  ) => {
    if (loading) {
      return (
        <div
          ref={ref}
          data-testid="lkc-skeleton"
          className={cn(
            landingKpiCardVariants({ accent }),
            "pointer-events-none",
            className,
          )}
          style={style}
        >
          <div className={kpiIconRowClassName}>
            <div
              className={cn(
                kpiSkeletonBoxClassName,
                "h-9 w-9 rounded-[var(--radius-md)]",
              )}
            />
            <div className={cn(kpiSkeletonBoxClassName, "h-5 w-[60px]")} />
          </div>
          <div className={cn(kpiSkeletonBoxClassName, "mb-2 h-8 w-[70px]")} />
          <div
            className={cn(kpiSkeletonBoxClassName, "mt-0.5 h-3.5 w-[120px]")}
          />
          <div className={cn(kpiSkeletonBoxClassName, "mt-2 h-3 w-[90px]")} />
        </div>
      )
    }

    const direction =
      trend?.direction === "up" || trend?.direction === "down"
        ? trend.direction
        : "flat"

    return (
      <div
        ref={ref}
        className={cn(landingKpiCardVariants({ accent }), className)}
        style={style}
      >
        <div className={kpiIconRowClassName}>
          <div className={kpiIconWrapVariants({ accent })}>{icon}</div>
          {trend?.label && (
            <span
              data-trend={direction}
              className={kpiTrendVariants({ direction })}
            >
              {trend.label}
            </span>
          )}
        </div>
        <div className="mb-1.5 text-[2rem] font-bold leading-none tracking-[-1px] text-[var(--text-heading)] tabular-nums">
          {value}
        </div>
        <div className="text-[length:var(--font-xs)] font-medium text-[var(--text-muted-2)]">
          {label}
        </div>
        {subNote && (
          <div
            data-testid="lkc-sub"
            className="mt-1.5 text-[length:var(--font-2xs)] text-[var(--text-muted-2)]"
          >
            {subNote}
          </div>
        )}
      </div>
    )
  },
)
LandingKPICard.displayName = "LandingKPICard"

export default LandingKPICard
export {
  landingKpiCardVariants,
  landingKpiGridClassName,
  landingKpiSectionLabelClassName,
}
